import React, { FC, useCallback, useEffect, useState } from 'react'
import { Divider, Grid, IconButton, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { SumsubApplicantStatusEnum } from '@sparling/financial-common'
import moment from 'moment'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { userPermissionEnum } from '../../../../constants/userPermissions'
import { icons } from '../../../../constants/icons'
import { KeyValue } from '../../../../components/Dashboard/KeyValue'
import { SingleLabel } from '../../../../components/Dashboard/SingleLabel'
import { MemoEditUserProfileWarningPopup } from './EditUserProfileWarningPopup'
import { MemoEditUserProfile } from './EditUserProfilePopup'
import { GeneralSectionProps } from '../General'
import { routes } from '../../../../constants/routes'
import { ApplyNewClientTypeModal } from '../General/ApplyNewClientTypeModal'
import { getClientTypesForAssignToUserRequest } from '../../../../store/actions'
import { GreenButton } from '../../../../components/Dashboard/Buttons/GreenButton'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 40,
    maxHeight: 40,
    paddingBottom: theme.spacing(2),
  },
  sectionWrap: {
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    padding: theme.spacing(4, 4, 2, 4),
    background: theme.palette.background.paper,
  },
  iconWrap: {
    display: 'inline-block',
    textAlign: 'right',
  },
  editIconGray: {
    color: '#a8a9ac',
    padding: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  divider: {
    backgroundColor: '#E9ECEF',
    opacity: '0.38',
    marginBottom: theme.spacing(2),
  },
}))

// eslint-disable-next-line
export const UserInfo: FC<GeneralSectionProps> = ({ title, loading, userAccount }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  // eslint-disable-next-line
  const [userInfoData, setUserInfoData] = useState<any>({})
  const [isOpenModalEditUserProfileWarning, setOpenModalEditUserProfileWarning] = useState<boolean>(false)
  const [isOpenModalEditUserProfile, setOpenModalEditUserProfile] = useState<boolean>(false)
  const [clearStep, setClearStep] = useState<string>('')
  const [userIdSf, setUserIdSf] = useState<string>('')
  const [openNewClientTypeModal, setOpenNewClientTypeModal] = useState<boolean>(false)

  useEffect(() => {
    if (userAccount) {
      setUserInfoData(userAccount)
      setUserIdSf(userAccount.idSf)
    }
  }, [userAccount])

  const toggleModalEditUserProfileWarning = useCallback(
    (isShowUserProfileScreen?) => {
      setOpenModalEditUserProfileWarning((prevState) => !prevState)
      if (isShowUserProfileScreen === true) {
        setOpenModalEditUserProfile(isShowUserProfileScreen)
      }
    },
    [setOpenModalEditUserProfileWarning],
  )

  const toggleModalEditUserProfile = useCallback(() => {
    setOpenModalEditUserProfile(false)
    setOpenModalEditUserProfile(false)
  }, [setOpenModalEditUserProfile])

  const onEdit = useCallback((stepClear: string) => {
    setOpenModalEditUserProfileWarning(true)
    setClearStep(stepClear)
  }, [])

  const openApplyNewClientType = useCallback(() => {
    dispatch(getClientTypesForAssignToUserRequest({ page: 1 }))
    setOpenNewClientTypeModal(true)
  }, [dispatch])
  const closeApplyNewClientType = useCallback(() => setOpenNewClientTypeModal(false), [])

  return (
    <>
      <Grid item xs={12} className={classes.formWrap}>
        <Grid item xs={12} className={classes.container}>
          <Typography variant={'h6'}>{title}</Typography>
        </Grid>

        <Grid container item xs={12} className={classes.sectionWrap}>
          <Grid item xs={11}>
            <KeyValue labelKeyValue={'Email'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.email || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Full Name'} minWidth="450px">
              <SingleLabel labelValue={`${userInfoData.firstName} ${userInfoData.lastName}` || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Nationality'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.nationality || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Phone'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.phone || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Date of Birth'} minWidth="450px">
              <SingleLabel labelValue={moment(userInfoData.birthday).format('DD/MM/YYYY') || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Gender'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.gender || '-'} />
            </KeyValue>
          </Grid>

          {(userInfoData.role === userPermissionEnum.admin || userInfoData.role === userPermissionEnum.sole_trader) &&
            pathname === routes.ACCOUNT_ADMIN && (
              <Grid item xs={1} className={classes.iconWrap}>
                <IconButton className={classes.editIconGray} onClick={() => onEdit(SumsubApplicantStatusEnum.IDENTITY)}>
                  <span className="material-icons">{icons.EDIT}</span>
                </IconButton>
              </Grid>
            )}

          {(userInfoData.role === userPermissionEnum.admin || userInfoData.role === userPermissionEnum.sole_trader) &&
            pathname === routes.ACCOUNT_ADMIN && (
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>
            )}

          <Grid item xs={11}>
            <KeyValue labelKeyValue={'Country'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.country || '-'} />
            </KeyValue>

            <KeyValue labelKeyValue={'Address'} minWidth="450px">
              <SingleLabel labelValue={userInfoData.address || '-'} />
            </KeyValue>
          </Grid>

          {(userInfoData.role === userPermissionEnum.admin || userInfoData.role === userPermissionEnum.sole_trader) &&
            pathname === routes.ACCOUNT_ADMIN && (
              <Grid item xs={1} className={classes.iconWrap}>
                <IconButton
                  className={classes.editIconGray}
                  onClick={() => onEdit(SumsubApplicantStatusEnum.PROOF_OF_RESIDENCE)}
                >
                  <span className="material-icons">{icons.EDIT}</span>
                </IconButton>
              </Grid>
            )}

          {userInfoData.clientTypeId && userInfoData.clientType && (
            <>
              <Grid item xs={12}>
                <Divider className={classes.divider} />
              </Grid>

              <Grid item xs={9} display={'flex'} alignItems={'center'}>
                <KeyValue labelKeyValue={'Client Type'} minWidth="450px" pb={'0px'}>
                  <SingleLabel labelValue={userInfoData.clientType} />
                </KeyValue>
              </Grid>
              <Grid item xs={3} display={'flex'} justifyContent={'flex-end'}>
                <GreenButton text={'Apply for New Type'} width={200} onClick={openApplyNewClientType} />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      <MemoEditUserProfileWarningPopup
        isOpen={isOpenModalEditUserProfileWarning}
        modalClose={toggleModalEditUserProfileWarning}
      />
      <MemoEditUserProfile
        isOpen={isOpenModalEditUserProfile}
        modalClose={toggleModalEditUserProfile}
        clearStep={clearStep}
        userIdSf={userIdSf}
      />
      <ApplyNewClientTypeModal
        isOpen={openNewClientTypeModal}
        modalClose={closeApplyNewClientType}
        currentClientType={userAccount?.clientType}
        currentClientTypeId={userAccount?.clientTypeId}
      />
    </>
  )
}
